import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

import './mobile-overrides.css';

// Auth
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import EmailVerification from './pages/EmailVerification';

// Dashboards
import StudentHome from './pages/Student/StudentHome';
import ParentDashboard from './components/Dashboards/ParentDashboard';
import AdminDashboard from './components/Dashboards/AdminDashboard';

// Games
import UniversalAIGames from './components/Games/UniversalAIGames';
import DragDropGames from './pages/Student/components/DragDropGames';
import AdvancedDragDropGames from './pages/Student/components/AdvancedDragDropGames';

// Lithuanian Language Center
import LithuanianLanguageCenter from './pages/LanguageGames/LithuanianLanguageCenter';

// Subscription
import SubscriptionPage from './pages/Subscription/SubscriptionPage';
import SubscriptionSuccess from './pages/Subscription/SubscriptionSuccess';
import SubscriptionCancel from './pages/Subscription/SubscriptionCancel';

// AI Test Solver
import AITestSolver from './components/AITestSolver';

// Security
import ContentProtection from './components/Common/ContentProtection';

// Landing
import LandingPage from './pages/LandingPage';

// Informational
import Contacts from './pages/Informational/Contacts';
import Terms from './pages/Informational/Terms';
import Privacy from './pages/Informational/Privacy';

// SEO
import RouteSEO from './components/SEO/RouteSEO';
import {
  EducationalGamesPage,
  MathGamesPage,
  ReadingGamesPage,
  LithuanianLanguageGamesPage,
  SpeechGamesPage,
  LogicalThinkingPage,
  NumbersCountingPage,
  VocabularyPage,
  ReadingComprehensionPage,
  AttentionPage,
  ScienceGamesPage,
  CreativeGamesPage,
  PreschoolGamesPage,
  PrimarySchoolGamesPage,
} from './pages/SEO/SEOCategoryPages';
import SEOGamePage from './pages/SEO/SEOGamePage';
import ContentHubPage from './pages/SEO/ContentHubPage';
import ContentLandingPage from './pages/SEO/ContentLandingPage';

// Common Components
import CookieConsent from './components/Common/CookieConsent';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const token = localStorage.getItem('access_token');

    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/auth/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUser(data);
        } else {
          localStorage.removeItem('access_token');
        }
      } catch (error) {
        console.error('Failed to load user:', error);
      }
    }

    setLoading(false);
  };

  const getDashboard = () => {
    if (!user) {
      return <Navigate to="/" />;
    }

    switch (user.role) {
      case 'admin':
        return <AdminDashboard />;

      case 'parent':
        return <ParentDashboard />;

      case 'student':
      default:
        return <StudentHome user={user} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-8xl mb-4 animate-bounce">🎮</div>
          <h2 className="text-3xl font-bold text-gray-800">
            Kraunama...
          </h2>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <RouteSEO />

      <ContentProtection />

      <Routes>
        {/* Auth */}
        <Route
          path="/login"
          element={
            !user ? (
              <Login onLogin={setUser} />
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/register"
          element={
            !user ? (
              <Register />
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/verify-email"
          element={<EmailVerification />}
        />

        {/* Landing Page */}
        <Route
          path="/"
          element={
            !user ? (
              <LandingPage />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />

        {/* =========================================================
            SEO LANDING PAGES
            ========================================================= */}

        <Route
          path="/mokomieji-zaidimai"
          element={<EducationalGamesPage />}
        />

        <Route
          path="/matematikos-zaidimai"
          element={<MathGamesPage />}
        />

        <Route
          path="/skaitymo-zaidimai"
          element={<ReadingGamesPage />}
        />

        <Route
          path="/lietuviu-kalbos-zaidimai"
          element={<LithuanianLanguageGamesPage />}
        />

        <Route
          path="/logopediniai-zaidimai"
          element={<SpeechGamesPage />}
        />

        {/* =========================================================
            STAGE 7 — AGE SEO HUBS
            ========================================================= */}

        <Route
          path="/zaidimai-ikimokyklinukams"
          element={<PreschoolGamesPage />}
        />

        <Route
          path="/zaidimai-pradinukams"
          element={<PrimarySchoolGamesPage />}
        />

        {/* =========================================================
            STAGE 6B — TOPIC SEO HUBS
            ========================================================= */}

        <Route
          path="/loginis-mastymas"
          element={<LogicalThinkingPage />}
        />

        <Route
          path="/skaiciai-ir-skaiciavimas"
          element={<NumbersCountingPage />}
        />

        <Route
          path="/zodynas"
          element={<VocabularyPage />}
        />

        <Route
          path="/teksto-supratimas"
          element={<ReadingComprehensionPage />}
        />

        <Route
          path="/demesys"
          element={<AttentionPage />}
        />

        <Route
          path="/gamtos-mokslu-zaidimai"
          element={<ScienceGamesPage />}
        />

        <Route
          path="/kurybiniai-zaidimai"
          element={<CreativeGamesPage />}
        />

        {/* =========================================================
            STAGE 8D — EDUCATIONAL CONTENT HUBS
            ========================================================= */}

        <Route
          path="/mokymosi-gidai"
          element={<ContentHubPage />}
        />

        <Route
          path="/mokymosi-gidai/:slug"
          element={<ContentLandingPage />}
        />

        {/* Individual SEO game pages */}
        <Route
          path="/zaidimai/:slug"
          element={<SEOGamePage />}
        />

        {/* Dashboards */}
        <Route
          path="/dashboard"
          element={getDashboard()}
        />

        {/* AI Test Solver */}
        <Route
          path="/ai-test"
          element={
            user ? (
              <AITestSolver />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Games */}
        <Route
          path="/ai-games"
          element={
            user ? (
              <UniversalAIGames user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path="/games/drag-drop"
          element={
            user ? (
              <DragDropGames user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path="/games/advanced"
          element={
            user ? (
              <AdvancedDragDropGames user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Lithuanian Language Center */}
        <Route
          path="/lietuviu-kalba"
          element={
            user ? (
              <LithuanianLanguageCenter />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Subscription */}
        <Route
          path="/subscription"
          element={<SubscriptionPage />}
        />

        <Route
          path="/subscription/success"
          element={
            user ? (
              <SubscriptionSuccess />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path="/subscription/cancel"
          element={
            user ? (
              <SubscriptionCancel />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Informational */}
        <Route
          path="/kontaktai"
          element={<Contacts />}
        />

        <Route
          path="/taisykles"
          element={<Terms />}
        />

        <Route
          path="/privatumas"
          element={<Privacy />}
        />
      </Routes>

      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
